import { CloseRounded } from "@mui/icons-material";
import {
  Button,
  createTheme,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import { opportunityRepo } from "@repositories";
import {
  clampMatchWeight,
  isValidMatchWeightInput,
  MATCH_WEIGHT_MAX,
  MATCH_WEIGHT_MIN,
  MatchWeightPromptCancelled,
  resolveMatchWeightForAdd as resolveMatchWeightForAddCore,
} from "@shared/helpers/match-weight";
import { useState } from "react";
import { useDialog } from "../contexts/DialogContext";

export { MatchWeightPromptCancelled };

const theme = createTheme({
  palette: {
    primary: {
      main: "#05204C",
    },
    secondary: {
      main: "#00b4d8",
    },
  },
});

function MatchWeightPrompt({
  initial,
  resolve,
}: {
  initial: number;
  resolve: (value: number | null) => void;
}) {
  const [value, setValue] = useState(String(initial));
  const parsed = Number(value);
  const valid = isValidMatchWeightInput(value);

  return (
    <ThemeProvider theme={theme}>
      <Tooltip placement="right" title="סגירה">
        <IconButton
          onClick={() => resolve(null)}
          sx={{
            position: "absolute",
            top: "2.5%",
            insetInlineEnd: "0.5%",
            zIndex: 2,
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <DialogTitle color="primary">מכפיל מאצ'ים</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <TextField
          type="number"
          placeholder="מכפיל"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputProps={{ min: MATCH_WEIGHT_MIN, max: MATCH_WEIGHT_MAX }}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ gap: "1vw" }}>
        <Button
          sx={{ textTransform: "none" }}
          variant="outlined"
          onClick={() => resolve(null)}
        >
          ביטול
        </Button>
        <Button
          sx={{ textTransform: "none" }}
          variant="contained"
          disabled={!valid}
          onClick={() => resolve(clampMatchWeight(parsed))}
        >
          אישור
        </Button>
      </DialogActions>
    </ThemeProvider>
  );
}

export function usePromptMatchWeight() {
  const dialog = useDialog();
  return (initial: number) =>
    dialog<number | null>(
      (resolve) => (
        <MatchWeightPrompt
          initial={clampMatchWeight(initial)}
          resolve={resolve}
        />
      ),
      null,
    );
}

export default usePromptMatchWeight;

export async function resolveMatchWeightForAdd(params: {
  opportunityId: string;
  defaultMatchWeight?: number;
  matchWeight?: number;
  prompt: (initial: number) => Promise<number | null | undefined>;
}): Promise<number | undefined> {
  return resolveMatchWeightForAddCore({
    ...params,
    loadDefaultMatchWeight: async (opportunityId) => {
      const opportunity = await opportunityRepo.findId(opportunityId);
      return opportunity?.defaultMatchWeight ?? 1;
    },
  });
}
